// Resolvedor de contexto (Parte 14.7, regras 1-7; REQ-FE-001..004). Função pura: entrada `/me` + preferência lembrada.
import type { MeContext, MeResponse, MeSite } from '@giyus/api-client';

import type { ContextPreference } from './contextPreferences';

export type ResolverInput = Pick<MeResponse, 'networkAccess' | 'contexts' | 'defaultContextHint'>;

export type Landing =
  | { readonly kind: 'no-access' }
  | { readonly kind: 'network' }
  | { readonly kind: 'tenant'; readonly tenantCode: string; readonly siteId: string | null }
  | { readonly kind: 'select-context' };

export const ROUTES = {
  noAccess: '/no-access',
  network: '/network',
  selectContext: '/select-context',
  account: '/me',
  tenant: (tenantCode: string) => `/t/${encodeURIComponent(tenantCode)}`,
  study: (tenantCode: string, studyCode: string) =>
    `/t/${encodeURIComponent(tenantCode)}/s/${encodeURIComponent(studyCode)}`,
} as const;

/** Regra 1: acesso de rede vem só de `networkAccess.hasAccess` (nunca de claims nem de papéis de tenant). */
export function hasNetworkAccess(me: Pick<MeResponse, 'networkAccess'>): boolean {
  return me.networkAccess.hasAccess;
}

/** Regra 7: `tenantCode` fora de `contexts[]` → `undefined` (a rota cai em NotFound local, sem chamar a API). */
export function findContext(
  me: Pick<MeResponse, 'contexts'>,
  tenantCode: string | null | undefined,
): MeContext | undefined {
  if (!tenantCode) {
    return undefined;
  }
  return me.contexts.find((context) => context.code === tenantCode);
}

export function findSite(context: MeContext | undefined, siteId: string | null): MeSite | null {
  if (!context || !siteId) {
    return null;
  }
  return context.sites.find((site) => site.id === siteId) ?? null;
}

/** Regra 4: um único site é selecionado automaticamente. */
export function defaultSiteId(
  context: MeContext,
  preferredSiteId: string | null = null,
): string | null {
  if (preferredSiteId && context.sites.some((site) => site.id === preferredSiteId)) {
    return preferredSiteId;
  }
  return context.sites.length === 1 ? (context.sites[0]?.id ?? null) : null;
}

/**
 * Matriz de pouso 2×3 (REQ-AUTH-028 / REQ-FE-001):
 *
 * | contextos | sem rede        | com rede                         |
 * | 0         | /no-access      | /network (única opção)           |
 * | 1         | /t/{code}       | /t/{code} + item "Rede"          |
 * | N         | /select-context | /select-context + item "Rede"    |
 *
 * Com N contextos, a última escolha lembrada (ou `defaultContextHint`) vale só se ainda estiver em `contexts[]`.
 * A rede nunca é o pouso padrão quando existe um contexto de tenant.
 */
export function resolveLanding(
  me: ResolverInput,
  remembered: ContextPreference | null = null,
): Landing {
  const network = hasNetworkAccess(me);
  if (me.contexts.length === 0) {
    return network ? { kind: 'network' } : { kind: 'no-access' };
  }
  if (me.contexts.length === 1) {
    const only = me.contexts[0];
    if (!only) {
      return network ? { kind: 'network' } : { kind: 'no-access' };
    }
    return {
      kind: 'tenant',
      tenantCode: only.code,
      siteId: defaultSiteId(only, remembered?.tenantCode === only.code ? remembered.siteId : null),
    };
  }
  const rememberedContext = findContext(me, remembered?.tenantCode);
  if (rememberedContext) {
    return {
      kind: 'tenant',
      tenantCode: rememberedContext.code,
      siteId: defaultSiteId(rememberedContext, remembered?.siteId ?? null),
    };
  }
  const hinted = findContext(me, me.defaultContextHint);
  if (hinted) {
    return { kind: 'tenant', tenantCode: hinted.code, siteId: defaultSiteId(hinted) };
  }
  if (me.defaultContextHint === 'NETWORK' && network) {
    return { kind: 'network' };
  }
  return { kind: 'select-context' };
}

export function landingPath(landing: Landing): string {
  switch (landing.kind) {
    case 'no-access':
      return ROUTES.noAccess;
    case 'network':
      return ROUTES.network;
    case 'select-context':
      return ROUTES.selectContext;
    case 'tenant':
      return ROUTES.tenant(landing.tenantCode);
  }
}

/** Regra 7 para rotas de rede: `/network/*` sem acesso → NotFound local (sem chamada à API). */
export function canEnterNetworkRoute(me: Pick<MeResponse, 'networkAccess'>): boolean {
  return hasNetworkAccess(me);
}

/** Regra 7 para rotas de tenant. */
export function canEnterTenantRoute(me: Pick<MeResponse, 'contexts'>, tenantCode: string): boolean {
  return findContext(me, tenantCode) !== undefined;
}

/** Item "Rede" no seletor: só com `hasAccess` (regras 5/6); a lista de tenants é `contexts[]` tal qual. */
export function selectorEntries(me: ResolverInput): {
  readonly showNetwork: boolean;
  readonly tenants: readonly MeContext[];
} {
  return { showNetwork: hasNetworkAccess(me), tenants: me.contexts };
}

export interface EffectiveScopeResolution {
  readonly isNetworkScope: boolean;
  readonly activeTenantCode: string | null;
  readonly activeTenantId: string | null;
  readonly activeSiteId: string | null;
  readonly hasScope: boolean;
}

/**
 * Resolve o escopo efetivo do shell: se o store já tem escopo ativo (rede ou tenant válido),
 * usa o store; caso contrário (ex.: acesso direto ou reload em `/me` e `/me/person`), deriva o
 * escopo padrão do usuário a partir de `/me` + preferência lembrada para que o menu lateral
 * (`Sidebar`) nunca desapareça nas telas de dados cadastrais / conta pessoal.
 */
export function resolveEffectiveScope(
  me: Pick<MeResponse, 'networkAccess' | 'contexts' | 'defaultContextHint'>,
  isNetworkScope: boolean,
  activeTenantCode: string | null,
  remembered: ContextPreference | null = null,
): EffectiveScopeResolution {
  if (isNetworkScope && hasNetworkAccess(me)) {
    return {
      isNetworkScope: true,
      activeTenantCode: null,
      activeTenantId: null,
      activeSiteId: null,
      hasScope: true,
    };
  }
  const currentTenant = findContext(me, activeTenantCode);
  if (currentTenant) {
    return {
      isNetworkScope: false,
      activeTenantCode: currentTenant.code,
      activeTenantId: currentTenant.tenantId,
      activeSiteId: defaultSiteId(
        currentTenant,
        remembered?.tenantCode === currentTenant.code ? remembered.siteId : null,
      ),
      hasScope: true,
    };
  }
  const landing = resolveLanding(me, remembered);
  if (landing.kind === 'network') {
    return {
      isNetworkScope: true,
      activeTenantCode: null,
      activeTenantId: null,
      activeSiteId: null,
      hasScope: true,
    };
  }
  if (landing.kind === 'tenant') {
    const target = findContext(me, landing.tenantCode);
    if (target) {
      return {
        isNetworkScope: false,
        activeTenantCode: target.code,
        activeTenantId: target.tenantId,
        activeSiteId: landing.siteId,
        hasScope: true,
      };
    }
  }
  if (hasNetworkAccess(me)) {
    return {
      isNetworkScope: true,
      activeTenantCode: null,
      activeTenantId: null,
      activeSiteId: null,
      hasScope: true,
    };
  }
  const firstTenant = me.contexts[0];
  if (firstTenant) {
    return {
      isNetworkScope: false,
      activeTenantCode: firstTenant.code,
      activeTenantId: firstTenant.tenantId,
      activeSiteId: defaultSiteId(firstTenant),
      hasScope: true,
    };
  }
  return {
    isNetworkScope: false,
    activeTenantCode: null,
    activeTenantId: null,
    activeSiteId: null,
    hasScope: false,
  };
}
